// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
/**
 * A folder setup's proposal and its acceptance (Job setup's "Save and use", automatic or not): one
 * copy of the rules, read by the setup screen (App.tsx) and by support's folder-setup actions
 * (main/support-folder-setup-actions.ts), so a job set up from a folder with nobody at the computer
 * (the Windows service) is the job the window would have made from the same proposal.
 *
 * Moved from App.tsx unchanged (applyBootstrapProposal and its merge helpers, isRemovedDocumentType);
 * `activatedSetupJob` is leaveSetup's activation, named.
 *
 * One import with a value, field-shape.ts, which has none: the main-process build and the support tests
 * load this file as it is.
 */

import type {
  BoundaryContinuationProfile,
  BoundaryOpeningProfile,
  FieldLookupAssignment,
  JobDocumentTypeDefinition,
  JobFieldDefinition,
  ProcessResult,
  RemovedDocumentType,
  TableReadingPlan,
} from '../shared/desktop-api';
import { fieldKinds, type FieldKind } from './field-shape';

/** A field as Job setup holds it. */
export type SetupField = {
  id: string;
  name: string;
  semanticId?: string | null;
  kind: FieldKind;
  required: boolean;
  readText: boolean;
  readBarcode: boolean;
  learnedFromCount?: number;
  readingInstruction?: string | null;
  tableReadingPlan?: TableReadingPlan | null;
  shiftLineItemColumns?: boolean;
  repairUnbalancedQuantity?: boolean;
  reviewBelow?: number | null;
  reviewBelowSource?: string | null;
  description: string;
  multiple: boolean;
  position: number;
  barcodeSymbology?: string | null;
  barcodeValuePrefix?: string | null;
  valuePattern?: string | null;
  formatExample?: string | null;
  defaultValue?: string | null;
  /** The lookup rules this field is subject to, as the engine stores them. */
  lookups?: FieldLookupAssignment[] | null;
};

/** A document type as Job setup holds it. */
export type SetupDocumentType = {
  id: string;
  name: string;
  semanticId?: string | null;
  description?: string | null;
  status: 'provisional' | 'active' | 'archived';
  schemaVersion: number;
  position: number;
  fields: SetupField[];
};

/** What these rules read of a job. */
export type SetupJob = {
  status: 'provisional' | 'active' | 'archived';
  bootstrapMode?: string;
  documentTypes: SetupDocumentType[];
  /** Types the operator removed; engine-owned, so saving never changes it. */
  removedDocumentTypes?: RemovedDocumentType[] | null;
};

export type BootstrapProposal = NonNullable<ProcessResult['bootstrapProposal']>;
type BootstrapJobFieldDefinition = JobFieldDefinition &
  Pick<
    SetupField,
    | 'learnedFromCount'
    | 'shiftLineItemColumns'
    | 'repairUnbalancedQuantity'
    | 'reviewBelow'
    | 'reviewBelowSource'
  >;

const knownFieldKinds: ReadonlySet<string> = new Set(fieldKinds);

const bootstrapField = (item: BootstrapJobFieldDefinition, position: number): SetupField => ({
  ...item,
  id: item.id,
  name: item.name,
  semanticId: item.semanticId ?? null,
  kind: knownFieldKinds.has(item.kind) ? (item.kind as FieldKind) : 'text',
  required: item.required,
  readText: item.readText,
  readBarcode: item.readBarcode,
  learnedFromCount: item.learnedFromCount,
  description: item.description ?? '',
  multiple: item.multiple,
  position,
  barcodeSymbology: item.barcodeSymbology ?? null,
  barcodeValuePrefix: item.barcodeValuePrefix ?? null,
  valuePattern: item.valuePattern ?? null,
  formatExample: item.formatExample ?? null,
  lookups: item.lookups ?? null,
  /* Carried to the reader, or the learner writes into a void. Everything else
     here is a shape a value must fit; this is the only thing that tells the
     reader where to look. */
  readingInstruction: item.readingInstruction ?? null,
  tableReadingPlan: item.tableReadingPlan,
  shiftLineItemColumns: item.shiftLineItemColumns,
  repairUnbalancedQuantity: item.repairUnbalancedQuantity,
  reviewBelow: item.reviewBelow,
  reviewBelowSource: item.reviewBelowSource,
});

const bootstrapDocumentType = (
  type: JobDocumentTypeDefinition,
  position: number,
): SetupDocumentType => ({
  id: type.id,
  name: type.name,
  semanticId: type.semanticId ?? null,
  description: type.description ?? null,
  status: type.status,
  schemaVersion: type.schemaVersion,
  position,
  fields: type.fields.map(bootstrapField),
});

const mergeUneditedValue = <T>(current: T, previous: T, proposed: T): T =>
  Object.is(current, previous) ? proposed : current;

const normalizedSerializable = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(normalizedSerializable);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.entries(value)
      .filter(([, item]) => item !== undefined)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, item]) => [key, normalizedSerializable(item)]),
  );
};
export const sameSerializableValue = (left: unknown, right: unknown) =>
  JSON.stringify(normalizedSerializable(left)) === JSON.stringify(normalizedSerializable(right));

const fieldStillMatchesBootstrap = (current: SetupField, previous: SetupField) =>
  sameSerializableValue(current, previous);

const documentTypeStillMatchesBootstrap = (
  current: SetupDocumentType,
  previous: SetupDocumentType,
) => {
  const { fields: currentFields, ...currentType } = current;
  const { fields: previousFields, ...previousType } = previous;
  return (
    sameSerializableValue(currentType, previousType) &&
    currentFields.length === previousFields.length &&
    currentFields.every((field, position) =>
      fieldStillMatchesBootstrap(field, previousFields[position]),
    )
  );
};

const mergeBootstrapField = (
  current: SetupField,
  previous: SetupField,
  proposed: SetupField,
): SetupField => ({
  ...current,
  // Each editable property has its own three-way decision. Renaming a field,
  // for example, must not prevent a later evidence-backed kind correction.
  name: mergeUneditedValue(current.name, previous.name, proposed.name),
  semanticId: mergeUneditedValue(current.semanticId, previous.semanticId, proposed.semanticId),
  kind: mergeUneditedValue(current.kind, previous.kind, proposed.kind),
  required: mergeUneditedValue(current.required, previous.required, proposed.required),
  readText: mergeUneditedValue(current.readText, previous.readText, proposed.readText),
  readBarcode: mergeUneditedValue(current.readBarcode, previous.readBarcode, proposed.readBarcode),
  learnedFromCount: mergeUneditedValue(
    current.learnedFromCount,
    previous.learnedFromCount,
    proposed.learnedFromCount,
  ),
  description: mergeUneditedValue(current.description, previous.description, proposed.description),
  multiple: mergeUneditedValue(current.multiple, previous.multiple, proposed.multiple),
  barcodeSymbology: mergeUneditedValue(
    current.barcodeSymbology,
    previous.barcodeSymbology,
    proposed.barcodeSymbology,
  ),
  barcodeValuePrefix: mergeUneditedValue(
    current.barcodeValuePrefix,
    previous.barcodeValuePrefix,
    proposed.barcodeValuePrefix,
  ),
  valuePattern: mergeUneditedValue(
    current.valuePattern,
    previous.valuePattern,
    proposed.valuePattern,
  ),
  formatExample: mergeUneditedValue(
    current.formatExample,
    previous.formatExample,
    proposed.formatExample,
  ),
  defaultValue: current.defaultValue,
  readingInstruction: mergeUneditedValue(
    current.readingInstruction,
    previous.readingInstruction,
    proposed.readingInstruction,
  ),
  tableReadingPlan: current.tableReadingPlan,
  shiftLineItemColumns: mergeUneditedValue(
    current.shiftLineItemColumns,
    previous.shiftLineItemColumns,
    proposed.shiftLineItemColumns,
  ),
  repairUnbalancedQuantity: mergeUneditedValue(
    current.repairUnbalancedQuantity,
    previous.repairUnbalancedQuantity,
    proposed.repairUnbalancedQuantity,
  ),
  reviewBelow: mergeUneditedValue(current.reviewBelow, previous.reviewBelow, proposed.reviewBelow),
  reviewBelowSource: mergeUneditedValue(
    current.reviewBelowSource,
    previous.reviewBelowSource,
    proposed.reviewBelowSource,
  ),
  lookups: sameSerializableValue(current.lookups, previous.lookups)
    ? proposed.lookups
    : current.lookups,
});

const sameSemanticIdentity = (
  left: { semanticId?: string | null },
  right: { semanticId?: string | null },
) =>
  Boolean(left.semanticId?.trim()) &&
  left.semanticId?.trim().toLowerCase() === right.semanticId?.trim().toLowerCase();

const normalizedBootstrapName = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '');
const sameBootstrapIdentity = (
  left: { name: string; semanticId?: string | null },
  right: { name: string; semanticId?: string | null },
) => {
  if (sameSemanticIdentity(left, right)) return true;
  if (left.semanticId?.trim() && right.semanticId?.trim()) return false;
  const normalizedName = normalizedBootstrapName(left.name);
  return Boolean(normalizedName) && normalizedName === normalizedBootstrapName(right.name);
};

const findUniqueBootstrapMatch = <T extends { name: string; semanticId?: string | null }>(
  target: T,
  candidates: T[],
): T | undefined => {
  const matches = candidates.filter((candidate) => sameBootstrapIdentity(target, candidate));
  return matches.length === 1 ? matches[0] : undefined;
};

const mergeBootstrapFields = (
  current: SetupField[],
  previous: SetupField[],
  proposed: SetupField[],
): SetupField[] => {
  const previousById = new Map(previous.map((field) => [field.id, field]));
  const proposedById = new Map(proposed.map((field) => [field.id, field]));
  const currentIds = new Set(current.map((field) => field.id));
  const deletedPrevious = previous.filter((field) => !currentIds.has(field.id));
  const consumedProposalIds = new Set<string>();
  const merged = current.flatMap((field) => {
    const previousField = previousById.get(field.id);
    const identityMatches = previousField
      ? proposed.filter((candidate) => sameBootstrapIdentity(previousField, candidate))
      : [];
    const previousIdentityIsUnique = previousField
      ? previous.filter((candidate) => sameBootstrapIdentity(previousField, candidate)).length === 1
      : false;
    const proposedField =
      proposedById.get(field.id) ??
      (previousField &&
        previousIdentityIsUnique &&
        findUniqueBootstrapMatch(
          previousField,
          proposed.filter(
            (candidate) =>
              !consumedProposalIds.has(candidate.id) && !previousById.has(candidate.id),
          ),
        ));
    if (!previousField) return [field]; // An operator-added field owns itself.
    if (proposedField) {
      consumedProposalIds.add(proposedField.id);
      return [mergeBootstrapField(field, previousField, proposedField)];
    }
    // Ambiguous generated identities cannot prove which field disappeared.
    // Keeping the current fields is the only non-destructive outcome.
    if (identityMatches.length > 0) return [field];
    return fieldStillMatchesBootstrap(field, previousField) ? [] : [field];
  });

  for (const field of proposed) {
    if (currentIds.has(field.id) || consumedProposalIds.has(field.id)) continue;
    // A changed generated ID must not resurrect a semantic field the operator
    // removed after the previous proposal.
    if (
      deletedPrevious.some(
        (deleted) => deleted.id === field.id || sameBootstrapIdentity(deleted, field),
      )
    )
      continue;
    if (current.some((existing) => sameBootstrapIdentity(existing, field))) continue;
    merged.push(field);
  }
  return merged.map((field, position) => ({ ...field, position }));
};

const mergeBootstrapDocumentType = (
  current: SetupDocumentType,
  previous: SetupDocumentType,
  proposed: SetupDocumentType,
): SetupDocumentType => ({
  ...current,
  name: mergeUneditedValue(current.name, previous.name, proposed.name),
  semanticId: mergeUneditedValue(current.semanticId, previous.semanticId, proposed.semanticId),
  description: mergeUneditedValue(current.description, previous.description, proposed.description),
  status: mergeUneditedValue(current.status, previous.status, proposed.status),
  schemaVersion: mergeUneditedValue(
    current.schemaVersion,
    previous.schemaVersion,
    proposed.schemaVersion,
  ),
  fields: mergeBootstrapFields(current.fields, previous.fields, proposed.fields),
});

const mergeBootstrapDocumentTypes = (
  current: SetupDocumentType[],
  previous: SetupDocumentType[],
  proposed: SetupDocumentType[],
): SetupDocumentType[] => {
  const previousById = new Map(previous.map((type) => [type.id, type]));
  const proposedById = new Map(proposed.map((type) => [type.id, type]));
  const currentIds = new Set(current.map((type) => type.id));
  const deletedPrevious = previous.filter((type) => !currentIds.has(type.id));
  const consumedProposalIds = new Set<string>();
  const merged = current.flatMap((type) => {
    const previousType = previousById.get(type.id);
    const identityMatches = previousType
      ? proposed.filter((candidate) => sameBootstrapIdentity(previousType, candidate))
      : [];
    const previousIdentityIsUnique = previousType
      ? previous.filter((candidate) => sameBootstrapIdentity(previousType, candidate)).length === 1
      : false;
    const proposedType =
      proposedById.get(type.id) ??
      (previousType &&
        previousIdentityIsUnique &&
        findUniqueBootstrapMatch(
          previousType,
          proposed.filter(
            (candidate) =>
              !consumedProposalIds.has(candidate.id) && !previousById.has(candidate.id),
          ),
        ));
    if (!previousType) return [type]; // An operator-added type owns itself.
    if (proposedType) {
      consumedProposalIds.add(proposedType.id);
      return [mergeBootstrapDocumentType(type, previousType, proposedType)];
    }
    if (identityMatches.length > 0) return [type];
    return documentTypeStillMatchesBootstrap(type, previousType) ? [] : [type];
  });

  for (const type of proposed) {
    if (currentIds.has(type.id) || consumedProposalIds.has(type.id)) continue;
    if (
      deletedPrevious.some(
        (deleted) => deleted.id === type.id || sameBootstrapIdentity(deleted, type),
      )
    )
      continue;
    if (current.some((existing) => sameBootstrapIdentity(existing, type))) continue;
    merged.push(type);
  }
  return merged.map((type, position) => ({
    ...type,
    position,
    fields: type.fields.map((field, fieldPosition) => ({ ...field, position: fieldPosition })),
  }));
};

/**
 * A type the operator removed stays removed: no setup proposal adds it back, by id or
 * by name, until they add it again by hand (DocumentStore.TypeRemoval).
 */
export const isRemovedDocumentType = (
  job: Pick<SetupJob, 'removedDocumentTypes' | 'documentTypes'>,
  type: { id: string; name: string },
) => {
  const name = type.name.trim().toLocaleLowerCase();
  if (
    job.documentTypes.some(
      (existing) => existing.id === type.id || existing.name.trim().toLocaleLowerCase() === name,
    )
  )
    return false;
  return (job.removedDocumentTypes ?? []).some(
    (removed) => removed.id === type.id || removed.name.trim().toLocaleLowerCase() === name,
  );
};

export const applyBootstrapProposal = <J extends SetupJob>(
  job: J,
  result: ProcessResult,
  previousProposal?: BootstrapProposal | null,
): J => {
  const proposed = (result.bootstrapProposal?.documentTypes ?? []).filter(
    (type) => !isRemovedDocumentType(job, type),
  );
  if (!proposed.length) return job;
  if (previousProposal?.documentTypes.length) {
    const previousTypes = previousProposal.documentTypes
      .filter((type) => !isRemovedDocumentType(job, type))
      .map(bootstrapDocumentType);
    const proposedTypes = proposed.map(bootstrapDocumentType);
    return {
      ...job,
      status: 'provisional',
      documentTypes: mergeBootstrapDocumentTypes(job.documentTypes, previousTypes, proposedTypes),
    };
  }
  const additions = proposed.filter(
    (type) => !job.documentTypes.some((existing) => existing.id === type.id),
  );
  if (job.documentTypes.length > 0 && additions.length === 0) return job;
  const sourceTypes = job.documentTypes.length === 0 ? proposed : additions;
  return {
    ...job,
    status: 'provisional',
    documentTypes: [
      ...job.documentTypes,
      ...sourceTypes.map((type, position) =>
        bootstrapDocumentType(type, job.documentTypes.length + position),
      ),
    ],
  };
};

/**
 * Save and use: a provisional job with types becomes active, and so does each type, unless a
 * destination owns the schema (a destination-owned type's status is part of that destination's
 * schema contract; activating the Paige job must not rewrite it). Anything else is unchanged.
 */
export const activatedSetupJob = <J extends SetupJob>(job: J, destinationOwnsSchema: boolean): J =>
  job.documentTypes.length > 0 && job.status === 'provisional'
    ? {
        ...job,
        status: 'active' as const,
        documentTypes: destinationOwnsSchema
          ? job.documentTypes
          : job.documentTypes.map((type) => ({ ...type, status: 'active' as const })),
      }
    : job;

/** New job's own folder job (CreateJobModal), before its setup has learned anything. */
export const newFolderSetupJob = (id: string, name: string, now: string) => ({
  id,
  name,
  description: 'Proposed from a folder of representative documents',
  accent: '#087f9c',
  bootstrapMode: 'folder' as const,
  status: 'provisional' as const,
  schemaVersion: 0,
  createdAt: now,
  updatedAt: now,
  documentTypes: [] as SetupDocumentType[],
  separationMode: 'automatic' as const,
  separationBarcodeText: null as string | null,
  boundaryOpeningProfiles: [] as BoundaryOpeningProfile[],
  boundaryContinuationProfiles: [] as BoundaryContinuationProfile[],
  suppressedSuggestions: [] as string[],
  suggestionsPausedUntil: null as string | null,
});
